import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchQuizzes, fetchLeaderboard } from "../Quiz/redux/quizSlice";
import { Skeleton } from "@mui/material";

const Leaderboard = () => {
  const dispatch = useDispatch();
  const { quizzes, leaderboard, loading, error } = useSelector(
    (state) => state.quiz
  );

  const [selectedQuiz, setSelectedQuiz] = useState("");
  const [visibleSteps, setVisibleSteps] = useState(0);

  useEffect(() => {
    dispatch(fetchQuizzes({ type: "1" }));
  }, [dispatch]);

  const handleQuizChange = (e) => {
    const quizId = e.target.value;
    setSelectedQuiz(quizId);
    setVisibleSteps(0);
    if (quizId) {
      dispatch(fetchLeaderboard({ quiz_id: quizId })).then(() => {
        // Animate reveal in 3 steps
        setTimeout(() => setVisibleSteps(1), 100);
        setTimeout(() => setVisibleSteps(2), 200);
        setTimeout(() => setVisibleSteps(3), 300);
      });
    }
  };

  return (
    <div className="leaderboard-container">
      {/* Inline CSS */}
      <style>{`
        .leaderboard-container {
          padding: 30px;
          text-align: center;
          font-family: 'Poppins', sans-serif;
        }

        .leaderboard-title {
          font-size: 26px;
          font-weight: bold;
          margin-bottom: 25px;
        }

        .quiz-select-container {
          display: flex;
          justify-content: center;
          margin-bottom: 20px;
        }

        .quiz-select {
          border: 1px solid #ccc;
          border-radius: 8px;
          padding: 10px 14px;
          width: 250px;
          font-size: 15px;
          outline: none;
          transition: 0.2s;
        }

        .quiz-select:focus {
          border-color: #6366f1;
          box-shadow: 0 0 5px #a5b4fc;
        }

        .loading {
          display: flex;
          justify-content: center;
          gap: 10px;
        }

        .error {
          color: red;
          margin-top: 10px;
        }

        /* Main Box */
        .leaderboard-box {
          width: 1000px;
          max-width: 95%;
          height: 400px;
          margin: 0 auto;
          background: radial-gradient(circle at top, #fff9db, #fff);
          border-radius: 20px;
          position: relative;
          box-shadow: 0 0 25px rgba(255, 215, 0, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          padding: 20px;
          gap: 20px;
        }

        .leaderboard-horizontal {
          display: flex;
          justify-content: center;
          align-items: flex-end;
          gap: 40px;
          width: 100%;
        }

        .rank-card {
          width: 150px;
          padding: 15px;
          border-radius: 15px;
          box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
          background: #fdfdfd;
          text-align: center;
          position: relative;
          opacity: 0;
        }

        .rank-card .medal {
          font-size: 36px;
          margin-bottom: 8px;
        }

        .rank-card .name {
          font-weight: 600;
          font-size: 16px;
        }

        .rank-card .score {
          font-size: 14px;
          color: #555;
        }

        /* Medal colors */
        .gold {
          background: linear-gradient(145deg, #ffe55e, #ffde00);
        }
        .silver {
          background: linear-gradient(145deg, #dcdcdc, #c0c0c0);
        }
        .bronze {
          background: linear-gradient(145deg, #d68c45, #c9712b);
          color: #fff;
        }

        /* Animation */
        .animate-pop {
          animation: popIn 0.6s ease-in-out forwards;
        }

        @keyframes popIn {
          0% {
            transform: scale(0.3) translateY(50px);
            opacity: 0;
          }
          100% {
            transform: scale(1) translateY(0);
            opacity: 1;
          }
        }

        .delay-1 {
          animation-delay: 0.6s;
        }
        .delay-2 {
          animation-delay: 1.2s;
        }

        /* Cracker / Sparkle effect */
        .crackers {
          position: absolute;
          top: -20px;
          left: 50%;
          width: 10px;
          height: 10px;
          background: radial-gradient(circle, gold, transparent);
          box-shadow:
            -80px -20px 10px gold,
            60px -30px 15px orange,
            -40px -50px 20px #ff0,
            50px -60px 25px #f90,
            -60px 10px 12px #ffd700,
            80px 0 15px #ffb700;
          border-radius: 50%;
          animation: sparkle 1.5s ease-in-out infinite alternate;
        }

        @keyframes sparkle {
          0% {
            transform: scale(0.8) rotate(0deg);
            opacity: 0.6;
          }
          100% {
            transform: scale(1.2) rotate(30deg);
            opacity: 1;
          }
        }

        .no-data {
          color: gray;
          font-size: 18px;
        }
      `}</style>

      <h2 className="leaderboard-title">🏆 Quiz Leaderboard</h2>

      {/* Quiz Dropdown */}
      <div className="quiz-select-container">
        <select
          className="quiz-select"
          value={selectedQuiz}
          onChange={handleQuizChange}
        >
          <option value="">Select Quiz</option>
          {quizzes?.map((quiz) => (
            <option key={quiz.id} value={quiz.id}>
              {quiz.title}
            </option>
          ))}
        </select>
      </div>

      {/* Loading Skeleton */}
      {loading && (
        <div className="loading">
          {[1, 2, 3].map((i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              width={120}
              height={150}
              style={{ borderRadius: 8 }}
            />
          ))}
        </div>
      )}

      {/* Error */}
      {/* {error && <p className="error">{error}</p>} */}
      {/* issue: `error` can be an object like { error: "..." } (server JSON payload from rejectWithValue), which crashes React: "Objects are not valid as a React child". Coerce to string. */}
      {error && (
        <p className="error">
          {typeof error === "string"
            ? error
            : error?.error || error?.message || "Something went wrong"}
        </p>
      )}

      {/* Leaderboard Display */}
      {!loading && selectedQuiz && (
        <div className="leaderboard-box">
          {leaderboard?.length > 0 ? (
            <div className="leaderboard-horizontal">
              {/* 2nd Place */}
              {visibleSteps >= 1 && leaderboard[1] && (
                <div className="rank-card silver animate-pop">
                  <div className="medal">🥈</div>
                  <div className="name">{leaderboard[1].user_name}</div>
                  <div className="score">{leaderboard[1].score}</div>
                </div>
              )}

              {/* 1st Place */}
              {visibleSteps >= 2 && leaderboard[0] && (
                <div className="rank-card gold animate-pop delay-1">
                  <div className="medal">🥇</div>
                  <div className="name">{leaderboard[0].user_name}</div>
                  <div className="score">{leaderboard[0].score}</div>
                  <div className="crackers"></div>
                </div>
              )}

              {/* 3rd Place */}
              {visibleSteps >= 3 && leaderboard[2] && (
                <div className="rank-card bronze animate-pop delay-2">
                  <div className="medal">🥉</div>
                  <div className="name">{leaderboard[2].user_name}</div>
                  <div className="score">{leaderboard[2].score}</div>
                </div>
              )}
            </div>
          ) : (
            <p className="no-data">No participants yet for this quiz.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default Leaderboard;
